import { NavLink, Link, useNavigate, useLocation } from 'react-router-dom';
import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Home,
  Compass,
  Sparkles,
  Users,
  Bell,
  Bookmark,
  Trophy,
  Settings,
  LogOut,
  LogIn,
  ChevronLeft,
  ChevronRight,
  Plus,
  PenSquare,
  BarChart3,
  UserCircle2,
  MessageSquareText
} from 'lucide-react';
import { cn } from '@/utils/cn';
import { useAuthStore } from '@/store/authStore';
import logo from '@/assets/Decixa_logo.png';

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

// Navigation items with colors
// Note: Home path is '/home' for authenticated feed, not '/' (landing page)
const mainNavItems = [
  { icon: Home, label: 'Home', path: '/home', color: '#6366F1', bgColor: '#EEF2FF' },
  { icon: Compass, label: 'Explore', path: '/explore', color: '#F59E0B', bgColor: '#FEF3C7' },
  { icon: Sparkles, label: 'Ask Decixa', path: '/ask-decixa', color: '#8B5CF6', bgColor: '#F3E8FF' },
  { icon: Users, label: 'Groups', path: '/groups', color: '#10B981', bgColor: '#D1FAE5' },
  { icon: Bell, label: 'Notifications', path: '/notifications', color: '#EF4444', bgColor: '#FEE2E2' },
];

const secondaryNavItems = [
  { icon: Bookmark, label: 'Bookmarks', path: '/bookmarks', color: '#F59E0B', bgColor: '#FEF3C7' },
  { icon: Trophy, label: 'Leaderboard', path: '/leaderboard', color: '#10B981', bgColor: '#D1FAE5' },
  { icon: Settings, label: 'Settings', path: '/settings', color: '#64748B', bgColor: '#F1F5F9' },
];

export function Sidebar({ collapsed, onToggle }: SidebarProps) {
  const { user, isAuthenticated, logout } = useAuthStore();
  const [showCreateMenu, setShowCreateMenu] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const createMenuRef = useRef<HTMLDivElement>(null);

  // Close create menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (createMenuRef.current && !createMenuRef.current.contains(event.target as Node)) {
        setShowCreateMenu(false);
      }
    }

    if (showCreateMenu) {
      // Add listener with a small delay to prevent immediate close
      const timeoutId = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
      }, 0);

      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showCreateMenu]);

  // Handle Home click - refresh if already on home, navigate if not
  const handleHomeClick = (e: React.MouseEvent) => {
    if (location.pathname === '/home') {
      e.preventDefault();
      // Subtle refresh - scroll to top and optionally trigger refetch
      window.scrollTo({ top: 0, behavior: 'smooth' });
      // You can also dispatch a custom event or use a store action to refresh feed
      window.dispatchEvent(new CustomEvent('refresh-home-feed'));
    }
    // If not on /home, NavLink will handle navigation normally
  };

  // Await the (async) logout before navigating: /login's AuthLayout redirects
  // authenticated users to /home, so navigating while the store still says
  // "logged in" bounces the user back mid-logout.
  const handleLogout = async () => {
    setShowLogoutConfirm(false);
    await logout();
    navigate('/login');
  };

  const handleLogin = () => {
    navigate('/login');
  };

  return (
    <aside className={cn('sidebar', collapsed ? 'sidebar--collapsed' : 'sidebar--expanded')}>

      <div className="sidebar__logo my-3">
        <Link to="/">
          <img src={logo} alt="Decixa" className="h-70" />
        </Link>
      </div>

      {/* Create Button - Only show when authenticated */}
      {isAuthenticated && (
        <div className="px-3 py-4">
          <div className="relative" ref={createMenuRef}>
            <button
              onClick={() => setShowCreateMenu(!showCreateMenu)}
              className={cn('sidebar__create-btn', collapsed && 'px-0')}
            >
              <Plus className="w-5 h-5" />
              <AnimatePresence>
                {!collapsed && (
                  <motion.span
                    initial={{ opacity: 0, width: 0 }}
                    animate={{ opacity: 1, width: 'auto' }}
                    exit={{ opacity: 0, width: 0 }}
                    className="overflow-hidden whitespace-nowrap"
                  >
                    Create
                  </motion.span>
                )}
              </AnimatePresence>
            </button>

            {/* Create Dropdown */}
            <AnimatePresence>
              {showCreateMenu && !collapsed && (
                <motion.div
                  initial={{ opacity: 0, y: -10, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -10, scale: 0.95 }}
                  transition={{ duration: 0.15 }}
                  className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-lg border border-slate-100 py-2 z-50"
                >
                  <Link
                    to="/create"
                    onClick={() => setShowCreateMenu(false)}
                    className="flex items-center gap-3 px-4 py-3 text-slate-700 hover:bg-slate-50 transition-colors"
                  >
                    <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center">
                      <PenSquare className="w-5 h-5 text-indigo-600" />
                    </div>
                    <div>
                      <p className="font-medium">New Decision</p>
                      <p className="text-xs text-slate-500">Get community feedback</p>
                    </div>
                  </Link>
                  <Link
                    to="/create-poll"
                    onClick={() => setShowCreateMenu(false)}
                    className="flex items-center gap-3 px-4 py-3 text-slate-700 hover:bg-slate-50 transition-colors"
                  >
                    <div className="w-10 h-10 rounded-xl bg-pink-50 flex items-center justify-center">
                      <BarChart3 className="w-5 h-5 text-pink-600" />
                    </div>
                    <div>
                      <p className="font-medium">New Poll</p>
                      <p className="text-xs text-slate-500">Quick multi-option vote</p>
                    </div>
                  </Link>

                  {/* Divider */}
                  <div className="my-2 border-t border-slate-100" />

                  {/* Ask Decixa Option */}
                  <Link
                    to="/ask-decixa"
                    onClick={() => setShowCreateMenu(false)}
                    className="flex items-center gap-3 px-4 py-3 text-slate-700 hover:bg-slate-50 transition-colors"
                  >
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center">
                      <MessageSquareText className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="font-medium">Ask Decixa</p>
                      <p className="text-xs text-slate-500">Get AI-powered advice</p>
                    </div>
                  </Link>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Collapsed state dropdown - positioned to the right */}
            <AnimatePresence>
              {showCreateMenu && collapsed && (
                <motion.div
                  initial={{ opacity: 0, x: -10, scale: 0.95 }}
                  animate={{ opacity: 1, x: 0, scale: 1 }}
                  exit={{ opacity: 0, x: -10, scale: 0.95 }}
                  transition={{ duration: 0.15 }}
                  className="absolute top-0 left-full ml-2 w-56 bg-white rounded-xl shadow-lg border border-slate-100 py-2 z-50"
                >
                  <Link
                    to="/create"
                    onClick={() => setShowCreateMenu(false)}
                    className="flex items-center gap-3 px-4 py-3 text-slate-700 hover:bg-slate-50 transition-colors"
                  >
                    <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center">
                      <PenSquare className="w-5 h-5 text-indigo-600" />
                    </div>
                    <div>
                      <p className="font-medium">New Decision</p>
                      <p className="text-xs text-slate-500">Get community feedback</p>
                    </div>
                  </Link>
                  <Link
                    to="/create-poll"
                    onClick={() => setShowCreateMenu(false)}
                    className="flex items-center gap-3 px-4 py-3 text-slate-700 hover:bg-slate-50 transition-colors"
                  >
                    <div className="w-10 h-10 rounded-xl bg-pink-50 flex items-center justify-center">
                      <BarChart3 className="w-5 h-5 text-pink-600" />
                    </div>
                    <div>
                      <p className="font-medium">New Poll</p>
                      <p className="text-xs text-slate-500">Quick multi-option vote</p>
                    </div>
                  </Link>

                  <div className="my-2 border-t border-slate-100" />

                  <Link
                    to="/ask-decixa"
                    onClick={() => setShowCreateMenu(false)}
                    className="flex items-center gap-3 px-4 py-3 text-slate-700 hover:bg-slate-50 transition-colors"
                  >
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center">
                      <MessageSquareText className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="font-medium">Ask Decixa</p>
                      <p className="text-xs text-slate-500">Get AI-powered advice</p>
                    </div>
                  </Link>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      )}

      {/* Main Navigation */}
      <nav className="sidebar__nav">
        {mainNavItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            onClick={item.path === '/home' ? handleHomeClick : undefined}
            className={({ isActive }) =>
              cn(
                'sidebar__nav-item',
                isActive && 'sidebar__nav-item--active',
                collapsed && 'justify-center px-0'
              )
            }
          >
            {({ isActive }) => (
              <>
                <div
                  className="sidebar__nav-icon p-2 rounded-lg transition-colors"
                  style={{
                    backgroundColor: isActive ? item.bgColor : 'transparent',
                  }}
                >
                  <item.icon
                    className="w-5 h-5"
                    style={{ color: item.color }}
                  />
                </div>
                <AnimatePresence>
                  {!collapsed && (
                    <motion.span
                      initial={{ opacity: 0, width: 0 }}
                      animate={{ opacity: 1, width: 'auto' }}
                      exit={{ opacity: 0, width: 0 }}
                      className="overflow-hidden whitespace-nowrap"
                    >
                      {item.label}
                    </motion.span>
                  )}
                </AnimatePresence>
              </>
            )}
          </NavLink>
        ))}

        {/* Divider */}
        <div className="my-4 border-t border-slate-100" />

        {/* Secondary Navigation */}
        {secondaryNavItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            className={({ isActive }) =>
              cn(
                'sidebar__nav-item',
                isActive && 'sidebar__nav-item--active',
                collapsed && 'justify-center px-0'
              )
            }
          >
            {({ isActive }) => (
              <>
                <div
                  className="sidebar__nav-icon p-2 rounded-lg transition-colors"
                  style={{
                    backgroundColor: isActive ? item.bgColor : 'transparent',
                  }}
                >
                  <item.icon
                    className="w-5 h-5"
                    style={{ color: item.color }}
                  />
                </div>
                <AnimatePresence>
                  {!collapsed && (
                    <motion.span
                      initial={{ opacity: 0, width: 0 }}
                      animate={{ opacity: 1, width: 'auto' }}
                      exit={{ opacity: 0, width: 0 }}
                      className="overflow-hidden whitespace-nowrap"
                    >
                      {item.label}
                    </motion.span>
                  )}
                </AnimatePresence>
              </>
            )}
          </NavLink>
        ))}
      </nav>

      {/* User Section - Show different content based on auth state */}
      <div className="sidebar__user">
        {isAuthenticated ? (
          <>
            {/* Authenticated User Profile */}
            <Link
              to="/profile"
              className={cn(
                'flex items-center gap-3 p-3 rounded-xl hover:bg-slate-50 transition-colors',
                collapsed && 'justify-center'
              )}
            >
              <img
                src={user?.avatar || 'https://api.dicebear.com/7.x/avataaars/svg?seed=default'}
                alt={user?.displayName || 'Profile'}
                className="w-10 h-10 rounded-full object-cover ring-2 ring-white shadow flex-shrink-0"
              />
              <AnimatePresence>
                {!collapsed && (
                  <motion.div
                    initial={{ opacity: 0, width: 0 }}
                    animate={{ opacity: 1, width: 'auto' }}
                    exit={{ opacity: 0, width: 0 }}
                    className="flex-1 min-w-0 overflow-hidden"
                  >
                    <p className="font-semibold text-slate-900 truncate">
                      {user?.displayName}
                    </p>
                    <p className="text-sm text-slate-500 truncate">
                      @{user?.username}
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>
            </Link>

            {/* Logout Button */}
            <AnimatePresence>
              {!collapsed && (
                <motion.button
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  onClick={() => setShowLogoutConfirm(true)}
                  className="flex items-center gap-3 w-full px-4 py-2.5 mt-2 text-slate-500 hover:text-rose-500 hover:bg-rose-50 rounded-xl transition-colors"
                >
                  <LogOut className="w-5 h-5" />
                  <span>Log out</span>
                </motion.button>
              )}
            </AnimatePresence>
          </>
        ) : (
          <>
            {/* Not Authenticated - Show Login */}
            <button
              onClick={handleLogin}
              className={cn(
                'flex items-center gap-3 p-3 rounded-xl bg-gradient-to-r from-primary to-secondary text-white hover:opacity-90 transition-all w-full',
                collapsed && 'justify-center'
              )}
            >
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                <UserCircle2 className="w-6 h-6" />
              </div>
              <AnimatePresence>
                {!collapsed && (
                  <motion.div
                    initial={{ opacity: 0, width: 0 }}
                    animate={{ opacity: 1, width: 'auto' }}
                    exit={{ opacity: 0, width: 0 }}
                    className="flex-1 min-w-0 overflow-hidden text-left"
                  >
                    <p className="font-semibold truncate">Log In</p>
                    <p className="text-xs text-white/80 truncate">Access your account</p>
                  </motion.div>
                )}
              </AnimatePresence>
            </button>

            {/* Sign Up Link */}
            {!collapsed && (
              <Link
                to="/register"
                className="flex items-center justify-center gap-2 w-full px-4 py-2.5 mt-2 text-primary font-medium hover:bg-primary/5 rounded-xl transition-colors"
              >
                <LogIn className="w-5 h-5" />
                <span>Create Account</span>
              </Link>
            )}
          </>
        )}
      </div>

      {/* Logout Confirmation Modal */}
      <AnimatePresence>
        {showLogoutConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
            onClick={() => setShowLogoutConfirm(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl p-6 max-w-sm mx-4 shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="w-12 h-12 rounded-full bg-rose-100 flex items-center justify-center mx-auto mb-4">
                <LogOut className="w-6 h-6 text-rose-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 text-center mb-2">
                Log Out?
              </h3>
              <p className="text-slate-500 text-center mb-6">
                Are you sure you want to log out of your account?
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowLogoutConfirm(false)}
                  className="flex-1 px-4 py-2.5 rounded-xl border border-slate-200 text-slate-700 font-medium hover:bg-slate-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleLogout}
                  className="flex-1 px-4 py-2.5 rounded-xl bg-rose-500 text-white font-medium hover:bg-rose-600 transition-colors"
                >
                  Log Out
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Collapse Toggle Button */}
      <button onClick={onToggle} className="sidebar__toggle">
        {collapsed ? (
          <ChevronRight className="w-4 h-4 text-slate-500" />
        ) : (
          <ChevronLeft className="w-4 h-4 text-slate-500" />
        )}
      </button>
    </aside>
  );
}